<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import PageNumber from '@spectee/components/Atoms/PageNumber/index.vue';
import PaginationItem from '@spectee/components/Atoms/PaginationItem/index.vue';

defineExpose({
  name: 'PaginationBar',
});

interface Props {
  currentPage: number;
  maxDisplayPages: number; //ページャー表示数
  itemsPerPage: number; //1ページあたりの表示件数
  total: number;
  isPageNumber?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  currentPage: 1,
  maxDisplayPages: 5,
  itemsPerPage: 10,
  total: 100,
  isPageNumber: false,
});

const emit = defineEmits(['onClick:pageNumber']);
const total = ref(props.total);

const currentPage = ref(props.currentPage ? props.currentPage : 1);
const maxPage = computed(() => Math.ceil(total.value / props.itemsPerPage));

const isPrev = computed(() => currentPage.value > 1);
const isNext = computed(() => currentPage.value < maxPage.value);

const numberRange = computed(() => {
  const numbers: number[] = [];

  // 最初の5ページの場合
  if (currentPage.value <= props.maxDisplayPages) {
    for (let i = 1; i <= props.maxDisplayPages; i++) {
      if (i <= maxPage.value) {
        numbers.push(i);
      }
    }
  } else {
    // 現在のページが6以上の場合の開始ページを計算
    const group = Math.ceil(currentPage.value / props.maxDisplayPages);
    const start = (group - 1) * props.maxDisplayPages + 1;

    for (let i = start; i < start + props.maxDisplayPages; i++) {
      if (i <= maxPage.value) {
        numbers.push(i);
      }
    }
  }

  return numbers;
});

const shouldDisplayOmissionBefore = computed(() => {
  return currentPage.value > props.maxDisplayPages;
});

const shouldDisplayOmissionAfter = computed(() => {
  const groups = Math.ceil(maxPage.value / props.maxDisplayPages);
  const currentPageGroup = Math.ceil(currentPage.value / props.maxDisplayPages);

  // 現在のページが最後のグループの最後のページのとき省略記号は表示しない
  if (currentPageGroup === groups && currentPage.value === maxPage.value) {
    return false;
  }

  // 現在のページが最後のページグループよりも前にある場合、省略記号を表示する
  return currentPageGroup < groups;
});

const displayComponent = computed(() => {
  return props.isPageNumber ? PageNumber : PaginationItem;
});

const onClick = (pageNumber: number) => {
  emit('onClick:pageNumber', pageNumber);
};

watch(
  () => props.currentPage,
  newVal => {
    currentPage.value = newVal;
  }
);

watch(
  () => props.total,
  newval => {
    total.value = newval;
  }
);
</script>

<template>
  <div
    class="Pagination"
    :class="[{ 'Pagination--pageNumber': isPageNumber }]"
    v-if="maxPage > 1"
  >
    <div class="Pagination__numberInfo" v-if="displayComponent !== PageNumber">
      {{ currentPage > 1 ? (currentPage - 1) * itemsPerPage + 1 : 1 }}-{{
        currentPage < maxPage ? currentPage * itemsPerPage : total
      }}
      件目（合計{{ total }}件）
    </div>
    <div class="Pagination__area">
      <component
        :is="displayComponent"
        :isPrev="true"
        :num="currentPage - 1"
        class="Pagination__arrow"
        :class="{ 'Pagination__arrow--visible': isPrev }"
        @click="onClick(currentPage - 1)"
      />
      <ol class="Pagination__itemList">
        <li
          class="Pagination__item"
          v-if="numberRange.length && numberRange[0] !== 1"
        >
          <component :is="displayComponent" :num="1" @click.stop="onClick(1)" />
        </li>
        <li class="Pagination__item" v-if="shouldDisplayOmissionBefore">
          <component :is="displayComponent" :isOmission="true" />
        </li>
        <li class="Pagination__item" v-for="num in numberRange" :key="num">
          <component
            :is="displayComponent"
            :num="num"
            :isActive="num === currentPage"
            @click.stop="onClick(num)"
          />
        </li>
        <li class="Pagination__item" v-if="shouldDisplayOmissionAfter">
          <component :is="displayComponent" :isOmission="true" />
        </li>
        <li
          class="Pagination__item"
          v-if="
            numberRange.length &&
            numberRange[numberRange.length - 1] !== maxPage
          "
        >
          <component
            :is="displayComponent"
            :num="maxPage"
            @click.stop="onClick(maxPage)"
          />
        </li>
      </ol>
      <component
        :is="displayComponent"
        :isNext="true"
        :num="currentPage + 1"
        class="Pagination__arrow"
        :class="{ 'Pagination__arrow--visible': isNext }"
        @click="onClick(currentPage + 1)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;

.Pagination {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;

  &__numberInfo {
    grid-column: 1;
    font-size: $service-font-size-mp;
    color: $color-gray-60;
  }

  &__area {
    display: flex;
    grid-column: 2;
    align-items: center;
    justify-content: center;
  }

  &__arrow {
    visibility: hidden;

    &--visible {
      visibility: visible;
    }
  }

  &__itemList {
    display: flex;
    margin: 0 $service-unit * 2;
  }

  &--pageNumber &__itemList {
    margin: 0 $service-unit * 4;
  }

  &__item {
    margin: 0;
  }

  &__item + &__item {
    margin-left: $service-unit * 2;
  }
}
</style>
